import { useEffect, useState, type ReactNode } from "react"
import type { TrackReferenceOrPlaceholder } from "@livekit/components-react"
import { useMutation } from "@tanstack/react-query"
import {
  Camera,
  Eraser,
  MousePointer2,
  MousePointerClick,
  MoveUpRight,
  Pencil,
  Play,
  Redo2,
  Snowflake,
  Square,
  Trash2,
  Undo2,
} from "lucide-react"

import { cn } from "@/custolink-newdesign/lib/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/custolink-newdesign/ui/alert-dialog"
import { Switch } from "@/custolink-newdesign/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/custolink-newdesign/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/custolink-newdesign/ui/tooltip"

import { renderFrame } from "./annotations/opLog"
import { SHAPE_COLORS, STROKE_WIDTHS, type StrokeWidth } from "./annotations/shapes"
import type { AnnotationSession, AnnotationTool } from "./annotations/useAnnotationSession"
import type { FreezeController } from "./freeze/useFreezeController"
import { useRoomSync } from "./RoomSyncProvider"
import { renderSnapshotPng } from "./snapshot/renderSnapshotPng"

/**
 * SyncToolbar - the worker and customer controls for freeze, drawing, permission, undo/redo,
 * clear-all and snapshots (D-07, D-08, D-10, D-11, D-12, RS-11..RS-15). A floating bar at the top
 * centre of the stage; every control is min 44px and labelled on mobile.
 */

type BarLayout = "desktop" | "mobile"

const COLOR_LABELS = ["Red", "Yellow", "Green", "Blue", "White"] as const
const WIDTH_LABELS: Record<StrokeWidth, string> = { 2: "Thin", 4: "Medium", 8: "Thick" }
const FREEZE_STATUS_TEXT: Partial<Record<string, string>> = {
  requesting: "Freezing...",
  "waiting-for-source": "Waiting for the camera...",
  capturing: "Capturing...",
  uploading: "Sharing...",
}

function controlClass(layout: BarLayout, extra?: string) {
  return cn(
    "cl:flex cl:shrink-0 cl:flex-col cl:items-center cl:justify-center cl:gap-0.5 cl:rounded-lg cl:text-white cl:hover:bg-white/10 cl:disabled:opacity-40 cl:disabled:pointer-events-none",
    layout === "desktop"
      ? "cl:h-9 cl:flex-row cl:gap-1.5 cl:px-2.5 cl:text-xs"
      : "cl:min-h-[44px] cl:min-w-[44px] cl:px-1 cl:text-[10px]",
    extra,
  )
}

function labelTextClass(layout: BarLayout) {
  return layout === "desktop" ? undefined : "cl:leading-none"
}

/** Wraps a standalone (non-Radix-trigger) button with a tooltip label on mobile only. */
function MaybeTooltip(p: { layout: BarLayout; label: string; children: ReactNode }) {
  if (p.layout !== "mobile") {
    return <>{p.children}</>
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>{p.children}</TooltipTrigger>
      <TooltipContent>{p.label}</TooltipContent>
    </Tooltip>
  )
}

function ToolbarButton(p: {
  layout: BarLayout
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick?: () => void
  disabled?: boolean
  extraClassName?: string
}) {
  const Icon = p.icon
  return (
    <MaybeTooltip label={p.label} layout={p.layout}>
      <button
        aria-label={p.label}
        className={controlClass(p.layout, p.extraClassName)}
        disabled={p.disabled}
        type="button"
        onClick={p.onClick}
      >
        <Icon className="cl:size-4" />
        {p.layout === "desktop" && <span className={labelTextClass(p.layout)}>{p.label}</span>}
      </button>
    </MaybeTooltip>
  )
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable
}

export function SyncToolbar(p: {
  layout: BarLayout
  stageTrack: TrackReferenceOrPlaceholder | undefined
  freeze: FreezeController
  session: AnnotationSession
}) {
  const sync = useRoomSync()
  const [saveAttempt, setSaveAttempt] = useState(0)
  const frameId = p.session.frameId
  const activeFrame = sync.room?.activeFrame ?? null
  const frozen = activeFrame != null

  const saveSnapshotMutation = useMutation({
    mutationFn: async () => {
      const worker = sync.api.worker
      if (!worker || !frameId || !activeFrame) {
        throw new Error("Nothing to save.")
      }
      const confirmedShapes = renderFrame({ ...sync.opLog, pending: new Map() }, frameId)
      const blob = await renderSnapshotPng(sync.frameImageSrc(frameId), activeFrame, confirmedShapes)
      const file = new File([blob], "snapshot.png", { type: "image/png" })
      // maxConfirmedSeq is -1 when the session has no ops at all; the backend accepts
      // only 0 <= upToSeq <= its maxSeq, so clamp (a snapshot of zero annotations is 0).
      return worker.saveSnapshot(frameId, Math.max(0, sync.opLog.maxConfirmedSeq), file)
    },
  })

  const setPermissionMutation = useMutation({
    mutationFn: (granted: boolean) => {
      const worker = sync.api.worker
      if (!worker) {
        return Promise.reject(new Error("Only the worker can change this."))
      }
      return worker.setAnnotationPermission(granted)
    },
    onSuccess: (view) => sync.applyServerState(view),
  })

  const hasPendingLocalOps = Array.from(sync.opLog.pending.values()).some((entry) => entry.local)

  // Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z and Ctrl+Y redo, Delete/Backspace delete, Escape clears the
  // selection - desktop only, ignored while typing anywhere else on the page.
  useEffect(() => {
    if (p.layout !== "desktop" || !p.session.canDraw) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) {
        return
      }
      const meta = e.ctrlKey || e.metaKey
      const key = e.key.toLowerCase()
      if (meta && !e.shiftKey && key === "z") {
        e.preventDefault()
        p.session.undo()
      } else if ((meta && e.shiftKey && key === "z") || (e.ctrlKey && key === "y")) {
        e.preventDefault()
        p.session.redo()
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (p.session.selectedShapeId) {
          e.preventDefault()
          p.session.deleteSelected()
        }
      } else if (e.key === "Escape") {
        p.session.setSelectedShapeId(null)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [p.layout, p.session])

  const selectedShape = p.session.shapes.find((s) => s.id === p.session.selectedShapeId) ?? null
  const canDeleteSelected = selectedShape != null && p.session.canModify(selectedShape)
  const savePending = saveSnapshotMutation.isPending
  const saveShowsSuccess = saveSnapshotMutation.isSuccess

  const errorMessage = p.session.message ?? sync.lastError
  // The stage's top-left corner holds the "On stage" source menu and the top centre the
  // reconnect banner. Unfrozen the bar is only the freeze button, so it sits in the free
  // top-right corner. Frozen it grows to a dozen controls, wide enough to run off the stage
  // edge; so it drops below that top row, spans the stage width and wraps instead of being
  // clipped. On a phone it sits at the bottom of the stage, where a thumb reaches it, in one
  // scrollable row (the thumbnails own the top-right there).
  const barClassName = cn(
    "cl:absolute cl:z-20 cl:flex cl:items-center cl:gap-1 cl:rounded-xl cl:bg-neutral-950/85 cl:p-1 cl:text-white",
    p.layout === "desktop" &&
      (frozen
        ? "cl:top-14 cl:left-2 cl:right-2 cl:flex-wrap cl:justify-end"
        : "cl:top-2 cl:right-2"),
    p.layout === "mobile" &&
      "cl:bottom-2 cl:left-1/2 cl:-translate-x-1/2 cl:max-w-[calc(100%-1rem)] cl:overflow-x-auto",
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div className={barClassName}>
        {sync.role === "worker" && !frozen && (
          <>
            <ToolbarButton
              disabled={!p.freeze.canFreeze(p.stageTrack)}
              icon={Snowflake}
              label="Freeze"
              layout={p.layout}
              onClick={() => {
                if (p.stageTrack) {
                  p.freeze.freeze(p.stageTrack)
                }
              }}
            />
            {FREEZE_STATUS_TEXT[p.freeze.status] && (
              <span className="cl:px-1 cl:text-[11px] cl:text-white/80" role="status">
                {FREEZE_STATUS_TEXT[p.freeze.status]}
              </span>
            )}
            {p.freeze.error && <span className="cl:px-1 cl:text-[11px] cl:text-red-300">{p.freeze.error}</span>}
          </>
        )}

        {sync.role === "worker" && frozen && (
          <>
            <ToolbarButton icon={Play} label="Resume live" layout={p.layout} onClick={() => p.freeze.resumeLive()} />
            <ToolbarButton
              disabled={hasPendingLocalOps || savePending}
              icon={Camera}
              label="Save snapshot"
              layout={p.layout}
              onClick={() => {
                setSaveAttempt((n) => n + 1)
                saveSnapshotMutation.mutate()
              }}
            />
            {saveShowsSuccess && (
              <span className="cl:px-1 cl:text-[11px] cl:text-white/80" role="status">
                Snapshot saved
              </span>
            )}
            {saveSnapshotMutation.isError && (
              <span className="cl:flex cl:items-center cl:gap-1 cl:px-1 cl:text-[11px] cl:text-red-300">
                Could not save the snapshot.
                <button
                  key={saveAttempt}
                  className="cl:underline"
                  type="button"
                  onClick={() => saveSnapshotMutation.mutate()}
                >
                  Retry
                </button>
              </span>
            )}
          </>
        )}

        {sync.role === "worker" && (
          <label className="cl:flex cl:shrink-0 cl:items-center cl:gap-1.5 cl:px-2 cl:text-xs">
            <Switch
              checked={sync.room?.customerMayAnnotate === true}
              disabled={setPermissionMutation.isPending}
              onCheckedChange={(checked) => setPermissionMutation.mutate(checked)}
            />
            <span>Customer can draw</span>
          </label>
        )}

        {sync.role === "worker" && frozen && p.session.canDraw && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button aria-label="Clear all" className={controlClass(p.layout)} type="button">
                <Eraser className="cl:size-4" />
                {p.layout === "desktop" && <span>Clear all</span>}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all annotations on this frame?</AlertDialogTitle>
                <AlertDialogDescription>
                  Everyone&apos;s drawings on this frame are removed. You can undo this.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => p.session.clearAll()}>Clear all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {frozen && p.session.canDraw && (
          <>
            <ToggleGroup
              className="cl:shrink-0"
              type="single"
              value={p.session.tool}
              onValueChange={(value) => {
                if (value) {
                  p.session.setTool(value as AnnotationTool)
                }
              }}
            >
              <ToggleGroupItem aria-label="Pointer" className={controlClass(p.layout)} value="pointer">
                <MousePointer2 className="cl:size-4" />
                {p.layout === "desktop" && <span>Pointer</span>}
              </ToggleGroupItem>
              <ToggleGroupItem aria-label="Arrow" className={controlClass(p.layout)} value="arrow">
                <MoveUpRight className="cl:size-4" />
                {p.layout === "desktop" && <span>Arrow</span>}
              </ToggleGroupItem>
              <ToggleGroupItem aria-label="Rectangle" className={controlClass(p.layout)} value="rect">
                <Square className="cl:size-4" />
                {p.layout === "desktop" && <span>Rectangle</span>}
              </ToggleGroupItem>
              <ToggleGroupItem aria-label="Freehand" className={controlClass(p.layout)} value="freehand">
                <Pencil className="cl:size-4" />
                {p.layout === "desktop" && <span>Freehand</span>}
              </ToggleGroupItem>
              <ToggleGroupItem aria-label="Select" className={controlClass(p.layout)} value="select">
                <MousePointerClick className="cl:size-4" />
                {p.layout === "desktop" && <span>Select</span>}
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="cl:flex cl:shrink-0 cl:items-center cl:gap-1 cl:px-1">
              {SHAPE_COLORS.map((color, i) => (
                <button
                  key={color}
                  aria-label={COLOR_LABELS[i]}
                  aria-pressed={p.session.color === color}
                  className={cn(
                    "cl:size-6 cl:shrink-0 cl:rounded-full cl:border-2",
                    p.session.color === color ? "cl:border-white" : "cl:border-transparent",
                  )}
                  style={{ backgroundColor: color }}
                  type="button"
                  onClick={() => p.session.setColor(color)}
                />
              ))}
            </div>

            <ToggleGroup
              className="cl:shrink-0"
              type="single"
              value={String(p.session.width)}
              onValueChange={(value) => {
                if (value) {
                  p.session.setWidth(Number(value) as StrokeWidth)
                }
              }}
            >
              {STROKE_WIDTHS.map((w) => (
                <ToggleGroupItem key={w} aria-label={WIDTH_LABELS[w]} className={controlClass(p.layout)} value={String(w)}>
                  <span>{WIDTH_LABELS[w]}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <ToolbarButton
              disabled={!canDeleteSelected}
              icon={Trash2}
              label="Delete"
              layout={p.layout}
              onClick={() => p.session.deleteSelected()}
            />
            <ToolbarButton
              disabled={!p.session.canUndo}
              icon={Undo2}
              label="Undo"
              layout={p.layout}
              onClick={() => p.session.undo()}
            />
            <ToolbarButton
              disabled={!p.session.canRedo}
              icon={Redo2}
              label="Redo"
              layout={p.layout}
              onClick={() => p.session.redo()}
            />
          </>
        )}

        {sync.role === "customer" && frozen && (
          <span className="cl:px-2 cl:text-xs cl:text-white/80" role="status">
            {sync.canAnnotate ? "You can draw on this frame" : "The expert is annotating this frame"}
          </span>
        )}

        {errorMessage && (
          <div className="cl:flex cl:shrink-0 cl:items-center cl:gap-2 cl:rounded-md cl:bg-red-600/90 cl:px-2 cl:py-1 cl:text-xs" role="alert">
            <span>{errorMessage}</span>
            <button
              aria-label="Dismiss"
              className="cl:underline"
              type="button"
              onClick={() => {
                p.session.clearMessage()
                sync.clearError()
              }}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

export default SyncToolbar
